import React from 'react';
import { Check, CircleAlert, X } from 'lucide-react';

import type {
    ReminderCadence,
    ReminderCadenceNode,
    ReminderCadenceStatus,
} from './types';

type TrackPhase = 'done' | 'current' | 'todo';

const formatDate = (value: string): string => {
    if (!value) {
        return '';
    }
    const normalised = value.includes('T') ? value : `${value}T00:00:00`;
    const date = new Date(normalised);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleDateString('en-NZ', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const phaseFor = (node: ReminderCadenceNode): TrackPhase => {
    if (node.kind === 'Today') {
        return 'current';
    }
    if (
        node.status === 'Upcoming' ||
        node.status === 'Scheduled' ||
        node.status === 'None'
    ) {
        return node.kind === 'Origin' ? 'done' : 'todo';
    }
    return 'done';
};

const statusTone = (status: ReminderCadenceStatus): string => {
    switch (status) {
        case 'Sent':
            return 'text-[#51a450]';
        case 'Scheduled':
            return 'text-[#9B9B9B]';
        case 'Skipped':
        case 'ExpectedUnsent':
            return 'text-[#B88600]';
        case 'Failed':
            return 'text-[#F24C41]';
        default:
            return 'text-[#9B9B9B]';
    }
};

const TrackMarker = ({
    node,
    phase,
}: {
    node: ReminderCadenceNode;
    phase: TrackPhase;
}) => {
    if (phase === 'current') {
        return (
            <span className="relative flex h-3 w-3 items-center justify-center">
                <span className="absolute h-3 w-3 rounded-full bg-[#1b1b1b]/20" />
                <span className="h-2 w-2 rounded-full bg-[#1b1b1b]" />
            </span>
        );
    }

    if (phase === 'todo') {
        return (
            <span className="h-3 w-3 rounded-full border-2 border-[#C8C8C8] bg-white" />
        );
    }

    if (node.status === 'Sent') {
        return (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#51a450] text-white">
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
        );
    }

    if (
        node.status === 'Skipped' ||
        node.status === 'ExpectedUnsent' ||
        node.status === 'Failed'
    ) {
        return (
            <span
                className={`flex h-4 w-4 items-center justify-center rounded-full text-white ${
                    node.status === 'Failed' ? 'bg-[#F24C41]' : 'bg-[#E6B800]'
                }`}
            >
                <CircleAlert className="h-2.5 w-2.5" strokeWidth={3} />
            </span>
        );
    }

    return <span className="h-2.5 w-2.5 rounded-full bg-[#1b1b1b]" />;
};

const TrackRow = ({
    marker,
    linePhase,
    isLast,
    firstLine,
    extra,
}: {
    marker: React.ReactNode;
    linePhase: TrackPhase | null;
    isLast: boolean;
    firstLine: React.ReactNode;
    extra?: React.ReactNode;
}) => (
    <li className="relative flex gap-3">
        {!isLast ? (
            <span
                aria-hidden
                className="pointer-events-none absolute z-0"
                style={{
                    top: 10,
                    bottom: -10,
                    left: 8,
                    width: 0,
                    borderLeft:
                        linePhase === 'todo'
                            ? '2px dashed #D4D4D4'
                            : '2px solid #1b1b1b',
                    transform: 'translateX(-1px)',
                }}
            />
        ) : null}
        <div className="relative z-1 flex h-5 w-4 shrink-0 items-center justify-center">
            {marker}
        </div>
        <div className={`min-w-0 flex-1 ${isLast ? 'pb-1' : 'pb-7'}`}>
            {firstLine}
            {extra}
        </div>
    </li>
);

const TodayBubble = ({
    label,
    linePhase,
    isLast,
}: {
    label: string;
    linePhase: TrackPhase | null;
    isLast: boolean;
}) => (
    <TrackRow
        marker={
            <span className="relative flex h-3 w-3 items-center justify-center">
                <span className="absolute h-3 w-3 rounded-full bg-[#1b1b1b]/20" />
                <span className="h-2 w-2 rounded-full bg-[#1b1b1b]" />
            </span>
        }
        linePhase={linePhase}
        isLast={isLast}
        firstLine={
            <div className="flex h-5 items-center">
                <span className="relative inline-flex h-5 items-center rounded-full bg-[#1b1b1b] px-2.5 text-[12px] leading-5 font-medium text-white">
                    <span
                        aria-hidden
                        className="absolute top-1/2"
                        style={{
                            left: -6,
                            width: 0,
                            height: 0,
                            marginTop: -5,
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderRight: '6px solid #1b1b1b',
                        }}
                    />
                    {label}
                </span>
            </div>
        }
    />
);

const TrackStop = ({
    node,
    phase,
    linePhase,
    isLast,
}: {
    node: ReminderCadenceNode;
    phase: TrackPhase;
    linePhase: TrackPhase | null;
    isLast: boolean;
}) => {
    const titleMuted = phase === 'todo';
    const date = formatDate(node.triggerDate);
    const extra = (
        <>
            {node.statusLabel ? (
                <div
                    className={`pt-0.5 text-[12px] leading-4 font-medium ${statusTone(node.status)}`}
                >
                    {node.statusLabel}
                </div>
            ) : null}
            {node.kind === 'Reminder' && node.days > 0 ? (
                <div
                    className={`pt-0.5 text-[12px] leading-4 ${
                        titleMuted ? 'text-[#C8C8C8]' : 'text-[#9B9B9B]'
                    }`}
                >
                    {node.days} days after due
                </div>
            ) : null}
            {node.detail ? (
                <div className="pt-1 text-[12px] leading-4 text-[#6b6b6b]">
                    {node.detail}
                </div>
            ) : null}
        </>
    );

    return (
        <TrackRow
            marker={<TrackMarker node={node} phase={phase} />}
            linePhase={linePhase}
            isLast={isLast}
            firstLine={
                <div className="flex min-h-5 items-start justify-between gap-3">
                    <div
                        className={`min-w-0 text-[14px] leading-5 ${
                            titleMuted
                                ? 'font-medium text-[#9B9B9B]'
                                : 'font-medium text-[#1b1b1b]'
                        }`}
                    >
                        {node.label}
                    </div>
                    {date ? (
                        <div
                            className={`h-5 shrink-0 text-[12px] leading-5 tabular-nums ${
                                titleMuted ? 'text-[#C8C8C8]' : 'text-[#9B9B9B]'
                            }`}
                        >
                            {date}
                        </div>
                    ) : null}
                </div>
            }
            extra={extra}
        />
    );
};

export interface ReminderCadenceDrawerProps {
    open: boolean;
    companyName: string;
    cadence: ReminderCadence | null;
    loading: boolean;
    error: boolean;
    onClose: () => void;
}

export const ReminderCadenceDrawer = ({
    open,
    companyName,
    cadence,
    loading,
    error,
    onClose,
}: ReminderCadenceDrawerProps) => {
    const [mounted, setMounted] = React.useState(open);
    const [visible, setVisible] = React.useState(false);

    React.useLayoutEffect(() => {
        if (open) {
            setMounted(true);
            const reduce = window.matchMedia?.(
                '(prefers-reduced-motion: reduce)',
            )?.matches;
            if (reduce) {
                setVisible(true);
                return;
            }
            setVisible(false);
            requestAnimationFrame(() => setVisible(true));
        } else {
            setVisible(false);
        }
    }, [open]);

    React.useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (open) {
            window.addEventListener('keydown', onKey);
        }
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const handleTransitionEnd = () => {
        if (!visible && mounted) {
            setMounted(false);
        }
    };

    if (!mounted) {
        return null;
    }

    const titleName = cadence?.companyName || companyName;
    const phases = cadence?.nodes.map(phaseFor) ?? [];

    return (
        <>
            <button
                type="button"
                aria-label="Close payment overdue reminders"
                onClick={onClose}
                className={`fixed inset-0 z-40 border-0 bg-black/30 p-0 transition-opacity duration-300 ease-out ${
                    visible
                        ? 'cursor-default opacity-100'
                        : 'pointer-events-none opacity-0'
                }`}
            />
            <aside
                role="dialog"
                aria-modal="true"
                aria-label="Payment overdue reminders"
                onTransitionEnd={handleTransitionEnd}
                className={`fixed top-0 right-0 z-50 flex h-screen w-[400px] max-w-[100vw] flex-col border-l border-[#E7E7E7] bg-white shadow-[-10px_0_32px_rgba(0,0,0,0.10)] transition-transform duration-300 ease-out ${
                    visible ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[#E7E7E7] px-5 py-3">
                    <div className="min-w-0">
                        <div className="text-[16px] leading-5 font-medium text-[#1b1b1b]">
                            Payment Overdue Reminders
                        </div>
                        {titleName ? (
                            <div className="truncate pt-0.5 text-[12px] leading-4 text-[#9B9B9B]">
                                {titleName}
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#313131] hover:bg-black/6"
                    >
                        <X className="h-4 w-4" aria-hidden />
                    </button>
                </header>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
                    {error ? (
                        <p className="text-center text-[#9B9B9B]">
                            Unable to load payment overdue reminders.
                        </p>
                    ) : null}
                    {!error && loading ? (
                        <p className="text-center text-[#9B9B9B]">Loading…</p>
                    ) : null}
                    {!error && !loading && cadence ? (
                        <ol className="m-0 list-none p-0">
                            {cadence.nodes.map((node, index) => {
                                const linePhase = phases[index + 1] ?? null;
                                const isLast =
                                    index === cadence.nodes.length - 1;
                                if (node.kind === 'Today') {
                                    return (
                                        <TodayBubble
                                            key={`${node.kind}-${node.days}-${index}`}
                                            label={node.label}
                                            linePhase={linePhase}
                                            isLast={isLast}
                                        />
                                    );
                                }
                                return (
                                    <TrackStop
                                        key={`${node.kind}-${node.days}-${node.sequence}-${index}`}
                                        node={node}
                                        phase={phaseFor(node)}
                                        linePhase={linePhase}
                                        isLast={isLast}
                                    />
                                );
                            })}
                        </ol>
                    ) : null}
                    {!error && !loading && !cadence ? (
                        <p className="text-center text-[#9B9B9B]">
                            No payment overdue reminders for this payment.
                        </p>
                    ) : null}
                </div>
            </aside>
        </>
    );
};
